import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Loader2, MailIcon, PrinterIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import type {
  TransmissionStrategyConfig,
  TransmissionStrategySchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface ConfigureStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  providerId: string;
  requestId: string;
  currentStrategy: TransmissionStrategyConfig | null;
  totalSent: number;
  nextScheduledDate: Date | null;
  /** z-index override for the dialog (use when rendered inside another modal) */
  zIndex?: number;
  /** Whether this dialog is nested inside another modal */
  nested?: boolean;
}

type ChannelType = 'fax' | 'email';

const toChannelType = (channel: string | null | undefined): ChannelType => {
  if (channel === 'fax' || channel === 'email') return channel;
  return 'fax'; // Default to fax for unsupported channels
};

const INTERVAL_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 5, label: '5 days' },
  { value: 7, label: '7 days (1 week)' },
  { value: 10, label: '10 days' },
  { value: 14, label: '14 days (2 weeks)' },
];

const MAX_ATTEMPTS_OPTIONS = [
  { value: 2, label: '2 attempts' },
  { value: 3, label: '3 attempts' },
  { value: 4, label: '4 attempts' },
  { value: 5, label: '5 attempts' },
];

const CHANNEL_OPTIONS: {
  value: ChannelType;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'fax',
    label: 'Fax',
    icon: <PrinterIcon className="h-4 w-4" />,
  },
  {
    value: 'email',
    label: 'Email',
    icon: <MailIcon className="h-4 w-4" />,
  },
];

const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRelativeDate = (date: Date) => {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays > 0) return `in ${diffDays} days`;
  return '';
};

export const ConfigureStrategyModal = ({
  isOpen,
  onClose,
  matterId,
  providerId,
  requestId,
  currentStrategy,
  totalSent,
  nextScheduledDate,
  zIndex,
  nested,
}: ConfigureStrategyModalProps) => {
  const [enabled, setEnabled] = useState(currentStrategy?.enabled ?? true);

  const [channel, setChannel] = useState<ChannelType>(
    toChannelType(currentStrategy?.channel),
  );
  const [intervalDays, setIntervalDays] = useState(
    currentStrategy?.interval_days ?? 3,
  );
  const [maxAttempts, setMaxAttempts] = useState(
    currentStrategy?.max_attempts ?? 3,
  );

  const [configureStrategy, { isLoading }] =
    treatmentRecordRequestService.useTransmissionStrategy2Mutation();

  // Reset form when modal opens or strategy changes
  useEffect(() => {
    if (isOpen) {
      setEnabled(currentStrategy?.enabled ?? true);
      setChannel(toChannelType(currentStrategy?.channel));
      setIntervalDays(currentStrategy?.interval_days ?? 3);
      setMaxAttempts(currentStrategy?.max_attempts ?? 3);
    }
  }, [isOpen, currentStrategy]);

  const handleSave = useCallback(async () => {
    const payload: TransmissionStrategySchema = {
      enabled,
      channel,
      interval_days: intervalDays,
      max_attempts: maxAttempts,
    };

    try {
      await configureStrategy({
        matterId,
        providerId,
        requestId,
        transmissionStrategySchema: payload,
      }).unwrap();

      toast.success('Follow-up strategy updated');
      onClose();
    } catch (error) {
      console.error('Failed to update strategy:', error);
      toast.error('Failed to update strategy. Please try again.');
    }
  }, [
    enabled,
    channel,
    intervalDays,
    maxAttempts,
    configureStrategy,
    matterId,
    providerId,
    requestId,
    onClose,
  ]);

  const progressPercent = Math.min((totalSent / maxAttempts) * 100, 100);

  return (
    <Dialog open={isOpen} onClose={onClose} zIndex={zIndex} nested={nested}>
      <DialogBody>
        <DialogTitle>Follow-up Strategy</DialogTitle>
        <DialogDescription>
          Configure automatic follow-up transmissions for this request.
        </DialogDescription>

        <div className="mt-6 space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <span
                id="autoEnabledLabel"
                className="text-sm font-medium text-zinc-900"
              >
                Automatic Follow-ups
              </span>
              <p className="text-sm text-zinc-500 mt-0.5">
                Automatically send follow-ups on schedule
              </p>
            </div>
            <button
              aria-labelledby="autoEnabledLabel"
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                enabled ? 'bg-indigo-600' : 'bg-zinc-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Settings (only when enabled) */}
          {enabled && (
            <>
              <hr className="border-zinc-200" />

              {/* Interval */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="block">
                <span className="text-sm font-medium text-zinc-700 mb-2 block">
                  Send every
                </span>
                <select
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(Number(e.target.value))}
                  className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  {INTERVAL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* Channel */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="block">
                <span className="text-sm font-medium text-zinc-700 mb-2 block">
                  Via channel
                </span>
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as ChannelType)}
                  className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  {CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {/* Max attempts */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="block">
                <span className="text-sm font-medium text-zinc-700 mb-2 block">
                  Maximum attempts
                </span>
                <select
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                  className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  {MAX_ATTEMPTS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {/* Info banner */}
          <div className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 space-y-1">
              <p className="font-medium">Automatic follow-ups stop when:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                <li>Voice AI confirms provider acknowledged</li>
                <li>Request status advances</li>
                <li>Maximum attempts reached</li>
              </ul>
            </div>
          </div>

          {/* Current progress */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-600">Current progress:</span>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-24 rounded-full bg-zinc-200 overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-zinc-700 font-medium">
                  {totalSent}/{maxAttempts} sent
                </span>
              </div>
            </div>
            {nextScheduledDate && enabled && (
              <p className="text-sm text-zinc-500 mt-2">
                Next scheduled: {formatDate(nextScheduledDate)} (
                {formatRelativeDate(nextScheduledDate)})
              </p>
            )}
          </div>
        </div>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="indigo" onClick={handleSave} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
